import mapboxgl from "mapbox-gl";
import { ERiskSummaryType, IRagScore, RiskOverrides } from "./risks";
import { FilterOperator, GammaDataSourceRaw } from "@gamma/types.data";
import {
  GammaMapLayerType,
  GammaMapLegendDisplayMode,
  GammaMapLegendItemType,
  OrgMapType,
} from "./consts";

export type AnyProvider = (AutoaddressProvider | PreciselyProvider) & {
  component?: any;
};

export interface SaveUserRequest {
  username: string;
  role: string;
  organisationName: string;
}

export interface Thresholds {
  greaterThan: number;
  colour: string;
  message: string;
}
export interface CapacityWarnings {
  sumInsuredColumn: string;
  capacityText: string;
  remainingCapacityText: string;
  thresholds: Thresholds[];
}

export interface IAccumulationLookup {
  businessLine: string;
  dataSetType: string;
  sumInsuredColumnContext: string;
  ragScores: number[];
  capacityWarnings?: CapacityWarnings;
}

export interface IUser {
  username: string;
  email: string;
  role: string;
  groups: string[];
  organisationName?: string;
  coverHolder?: string;
  verifiedEmail: string;
  status?: string;
  lastLogin?: Date;
  createdAt?: string;
  updatedAt?: string;
}
export interface IAccumulationsSettings {
  defaultBuffer: number;
  buffers: number[];
  accumulationsLookups: IAccumulationLookup[];
  showInBottomPanel: boolean;
  showInLeftPanel: boolean;
  spatialFunction?: string;
  buildingCountLookup?: BuildingCountLookup;
}

export interface BuildingCountLookup {
  url: string;
  apiKey: string;
}

export interface BuildingCountResult {
  NoBuildings: number;
  NoRDPs: number;
  NoCDPs: number;
}

export interface OSLayoutSettings {
  companyLogo: string;
  showCoverHolder: boolean;
}

interface OSApplicationReports {
  defaultDistance: number;
  showAccumulations: boolean;
  showRisks: boolean;
  showPerils: boolean;
  showServices: boolean;
}

export interface IBaseMap {
  name: string;
  type: string;
  isDefault: boolean;
  style?: string;
  url?: string;
  licence?: string;
  licenceKey?: string;
  beforeId?: string;
  mapTypeId?: string;
  layerHooks?: Record<string, any>;
}

interface IAccumulationScore {
  green: number;
  amber: number;
  red: number;
}

export interface IAccumulationRange {
  value: number;
  isDefault: boolean;
  score: IAccumulationScore;
}

interface IWmsLayerStyle {
  name: string;
  displayName: string;
  isDefault: boolean;
}

interface IWmsLayer {
  name: string;
  layerId: string;
  category: string;
  styles: IWmsLayerStyle[];
  isVisible: boolean;
  opacity: number;
  url: string;
  displayColor: string;
  queryable: boolean;
  queryType: any;
  useLocalProxy: boolean;
  localProxyType: string;
  layerType: any;
}
export enum RiskLookupType {
  JBA = "JBA",
  Arson = "Arson",
  Subsidence = "Subsidence",
  Burglary = "Burglary",
  Other = "Other",
  FloodClimateChange = "FloodClimateChange",
  FloodClimateChangeChart = "FloodClimateChangeChart",
}

export interface IRiskLookup {
  name: string;
  description?: string;
  moreInfo?: string;
  licenceKey: string;
  url: string;
  profile: string;
  buffer: number;
  displayLocation?: string;
  type?: RiskLookupType;
  scenario: string;
  scenarios?: string[];
  coordinatesOnly: boolean;
  bufferType?: string;
}

export interface RiskChartResult {
  RiskSummaries: RiskSummary[];
  JBAScenarios: JBAScenarios[];
  Accuracy: Accuracy;
}

export interface JBAScenarios {
  Id: string;
  StartYear: string;
  EndYear: string;
}

export interface RiskSummary {
  Period: string;
  JBACombinedRisk: JbacombinedRisk;
  JBAScoreCount: any;
}

export interface JbacombinedRisk {
  CombinedScoreRiver: number;
  CombinedScoreSurfaceWater: number;
  CombinedScoreCoastal: number;
  CombinedScore: number;
  RAGScore: Ragscore;
}

export interface Ragscore {
  Level: number;
  Description: string;
  Color: string;
}

export interface Accuracy {
  ZoomstackBuilding: string;
  BuildingId: string;
  BuildingGeom: BuildingGeom;
  Level: string;
  Description: string;
}

export interface BuildingGeom {
  type: string;
  coordinates: number[][][][];
}

export interface ILocationDataLookup {
  name: string;
  property?: string;
  licenceKey: string;
  url: string;
  transform?: string;
  field?: LookupField;
}

export interface LookupField {
  dataCatId: number;
  fieldCatId: number;
}

export interface IGammaMapLegendItem {
  description: string;
  color: string;
}

export interface IMapLayerLegend {
  display: GammaMapLegendDisplayMode;
  min?: string;
  max?: string;
  legendItems: IGammaMapLegendItem[];
  type: GammaMapLegendItemType;
}

export interface IMapLayerGroup {
  type?: GammaMapLayerType;
  name: string;
  layers: (string | IMapLayerGroup)[];
  id?: string;
  icon?: string;
  description?: string;
  path?: string;
  parents?: string[];
  source?: string;
  legend?: IMapLayerLegend;
}

export interface IBusinessLine {
  name: string;
  type: string;
  default: boolean;
  accumulationRanges?: IAccumulationRange[];
  wmsLayers?: IWmsLayer[];
  locationDataLookups?: ILocationDataLookup[];
  riskLookups: IRiskLookup[];
  layerGroups: IMapLayerGroup[];
  riskLookupOverrides?: RiskOverrides;
  epcRating?: EPCRating;
}

export interface EPCRating {
  licenceKey: string;
  url: string;
  coverage?: string;
  country: string;
}

export interface ILayerControl {
  isStyleLayer?: boolean;
  id?: string;
  path?: string;
  opacity?: number;
  editable?: boolean;
  isVisible?: boolean;
  layerIds?: string[];
}

export interface OSMapSettings {
  mapOptions: mapboxgl.MapboxOptions;
  baseMaps: IBaseMap[];
  sources: GammaDataSourceRaw;
  layers: mapboxgl.Layer[];
  JBA?: {
    accuracyBuffer: mapboxgl.Layer;
  };
}

export interface OSAddressProvider {
  name: string;
  providerType: ProviderType;
  api: Record<string, string>;
  country: string;
  licenceKey: string;
}

export enum ProviderType {
  PitneyBowes = "PitneyBowes",
  AutoAddress = "AutoAddress",
  Data8 = "Data8",
}

export interface SearchMode {
  name: string;
  component: any;
  sourceName?: string;
  lcId?: string;
}

export enum SearchType {
  Address = "Address",
  Coordinates = "Coordinates",
  Dataset = "Dataset",
}

export interface OSApplicationSettings {
  showPolicies?: boolean;
  showClaims?: boolean;
  showCoverHolder?: boolean;
  defaultRadius?: number;
  showBuildingOutlines?: boolean;
  showLocalityOutlines?: boolean;
  defaultRiskLayerId?: any;
  defaultRiskLayerCommercialId?: any;
  defaultRiskLayerHouseholdId?: any;
  showSabIds?: boolean;
  reports?: OSApplicationReports;
  showAudit?: boolean;
  availablePages?: any;
  baseDataApiUrl?: string;
  searchModes: SearchType[];
  baseGroupsDataApiUrl?: string;
  useDatasetRecordGroups?: boolean;
  showRags: boolean;
  showRiskDetails: boolean;
  minifyRightSidebar: boolean;
  enableGeometryRisk?: boolean;
  enableBookUploaderRisk?: boolean;
  clientStyles?: { color: string; logo: string };
  currency?: string;
  showMeasuresOnGeometryRisk?: boolean;
  googleAPIKey?: string;
}

export interface PreciselyProvider extends OSAddressProvider {
  maxCandidates?: number;
  distance?: number;
  api: {
    base: string;
    autocomplete: string;
    geocode: string;
    reverseGeocode: string;
    gamma: string;
  };
}

export interface Data8Provider extends OSAddressProvider {
  maxCandidates?: number;
  distance?: number;
  api: {
    base: string;
    autocomplete: string;
    findAddress: string;
    applicationName: string;
  };
}

export interface AutoaddressProvider extends OSAddressProvider {
  api: {
    base: string;
    findAddress: string;
    getEcadData: string;
    getGBPostcodeData: string;
    getGBBuildingData: string;
    autocomplete: string;
    reverseGeocode: string;
    dynamicLicenceKey: string;
  };
}
export interface OSAddressSettings {
  providers: (AutoaddressProvider | PreciselyProvider)[];
}

export interface BookUploaderSettings {
  riskDataSets: RiskDataSet[];
}

export interface RiskDataSet {
  isSelected: boolean;
  id: string;
  name: string;
  description: string;
}

export interface Geocoder {
  country?: string;
  provider?: string;
}

export interface OrgSettings {
  layoutSettings?: OSLayoutSettings;
  applicationSettings?: OSApplicationSettings;
  bookUploaderSettings?: BookUploaderSettings;
  businessLines: IBusinessLine[];
  addressSettings?: OSAddressSettings;
  mapSettings: OSMapSettings;
  accumulations?: IAccumulationsSettings;
  dataDisplay?: DisplayData[];
  reportSettings?: ReportSettings;
}

export interface ReportSettings {
  displayLayers?: string[];
  useSelectedBasemap?: boolean;
  showFloodDetails?: boolean;
  defaultBasemapName: string;
}

export interface DisplayData {
  businessLine: string;
  columns: DisplayDataColumn[];
  dataSetType: string;
}
export interface DisplayDataColumn {
  columnName: string;
  displayName: string;
  selectedBottomPanel: boolean;
  showInBottomPanel: boolean;
  showInLeftPanel: boolean;
  supportedOperators: FilterOperator[];
}

export interface IOrganisation {
  applicationName: string;
  name: string;
  userLimit: number;
  availableMaps: {
    type: OrgMapType;
    label?: string;
    licenceKey?: string;
    isDisabled?: boolean;
  }[];
  layers: string[];
  organisationSettings: OrgSettings;
}

export interface MapPosition {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

export interface RiskScoreViewer {
  name: string;
  score: number;
  ragScore: IRagScore;
  icon?: string;
  html?: string;
  accuracy?: string;
  type: ERiskSummaryType;
}

export type DRAW_MODE =
  | "simple_select"
  | "draw_circle"
  | "direct_select"
  | "draw_polygon"
  | "draw_line_string"
  | "draw_point"
  | "static"
  | "draw_risk_polygon";

export interface MapDrawSelection {
  additive: boolean;
  mode: DRAW_MODE;
  icon?: string;
  title?: string;
}
export type MapDrawMenuItemOption =
  | "type"
  | "src"
  | "variant"
  | "icon"
  | "iconType"
  | "title"
  | "mode";

export interface MapDrawMenuItem
  extends Partial<Record<MapDrawMenuItemOption, string>> {
  action?: (o: any) => void;
}

export * from "./consts";
export * from "./User";
export * from "./Organisation";
export * from "./MapLayerGroup";
export * from "./LayerControl";
export * from "./RiskRecord";
export * from "./pitneyBowes";
export * from "./risks";
export * as GDT from "@gamma/types.data";
export * from "./Policy";
export * from "./Claim";
// export * from "./GammaDataSource";
export * from "./location";
export * from "./DatasetRecord";
export * from "./location/AddressLocation";
export * from "./location/DisplayItem";
export * from "./layout";
export * from "./data8";
export * from "./book";
export * from "./gamma-stepper";
export * from "./initSettings";
export interface IUserGroup {
  nextToken?: string;
  group: string;
  users: string[];
}

export interface PLevel {
  level: number;
  label: string;
  description: string;
}

export interface IDropdownItem<T> {
  key: any;
  text?: string;
  value: T;
}

export interface SearchProviderClient {
  providerType: string;
  api: Record<string, string>;
  licenceKey: string;
  country: string;
  maxCandidates?: number;
  distance?: number;
}
